// services/Applications.ts
import { ref } from 'vue';
import { sendGraphQLQuery } from '@/services/gql-util';
import { ApplicationInfo } from '@/types/Applications';

export interface ApplicationTemplate {
	id: string;
	name: string;
	url: string;
	appid: string;
	locked: string;
	locale: string;
	isLocked: boolean;
	modified: string;
	modifiedby: string;
}

export interface ApplicationTemplateContent {
	id: string;
	name: string;
	content: string | null;
	publicdata: string | null;
	privatedata: string | null;
	appid: string;
	locked: string | null;
	locale: string | null;
	modified: string | null;
	modifiedby: string | null;
}

export function useApplications() {
	const applications = ref<ApplicationInfo[]>([]);
	const templates = ref<ApplicationTemplate[]>([]);
	const loadingTemplates = ref(false);

	const getApplicationList = async (vhostId: string): Promise<ApplicationInfo[]> => {
		const query = `
		query ($vhostId: Int!) {
			Applications {
				applicationList(vhost: $vhostId) {
					id
					vhost
					name
					thumbUrl
					type
					maindomain
					class
					description
					app_system_name
					translator_language_default
					saml_entityid
					email
					settings {
						uploadsByDay {
							date
							count
						}
					}
				}
			}
		}
		`;

		const variables = { vhostId: parseInt(vhostId) };

		try {
			const data = await sendGraphQLQuery<{ Applications: { applicationList: ApplicationInfo[] } }>(query, variables);
			applications.value = data.Applications.applicationList;
			return data.Applications.applicationList;
		} catch (error) {
			console.error('Error fetching applications:', error);
			throw error;
		}
	};

	const getApplicationTemplates = async (applicationId: number, type: string = 'apptemplates'): Promise<ApplicationTemplate[]> => {
		const query = `
		query ($applicationId: Int!, $type: String) {
			Applications {
				applicationTemplates(applicationId: $applicationId, type: $type) {
					id
					name
					url
					appid
					locked
					locale
					isLocked
					modified
					modifiedby
				}
			}
		}
		`;

		const variables = { applicationId, type };
		loadingTemplates.value = true;

		try {
			const data = await sendGraphQLQuery<{ Applications: { applicationTemplates: ApplicationTemplate[] } }>(query, variables);
			templates.value = data.Applications.applicationTemplates;
			return data.Applications.applicationTemplates;
		} catch (error) {
			console.error('Error fetching templates:', error);
			throw error;
		} finally {
			loadingTemplates.value = false;
		}
	};

	const getTemplateContent = async (applicationId: number, type: string, name: string): Promise<ApplicationTemplateContent | null> => {
		const query = `
		query ($applicationId: Int!, $type: String!, $name: String!) {
			Applications {
				getTemplate(applicationId: $applicationId, type: $type, name: $name) {
					id
					name
					content
					publicdata
					privatedata
					appid
					locked
					locale
					modified
					modifiedby
				}
			}
		}
		`;

		const variables = { applicationId, type, name };

		try {
			const data = await sendGraphQLQuery<{ Applications: { getTemplate: ApplicationTemplateContent } }>(query, variables);
			return data.Applications.getTemplate;
		} catch (error) {
			console.error('Error fetching template content:', error);
			return null;
		}
	};

	const saveTemplateContent = async (applicationId: number, type: string, name: string, content: string): Promise<boolean> => {
		const query = `
		mutation ($applicationId: Int!, $type: String!, $name: String!, $content: String!) {
			ApplicationMutations {
				saveTemplate(applicationId: $applicationId, type: $type, name: $name, content: $content)
			}
		}
		`;

		const variables = { applicationId, type, name, content };

		try {
			const data = await sendGraphQLQuery<{ ApplicationMutations: { saveTemplate: boolean } }>(query, variables);
			return data.ApplicationMutations.saveTemplate;
		} catch (error) {
			console.error('Error saving template:', error);
			return false;
		}
	};

	return {
		applications,
		templates,
		loadingTemplates,
		getApplicationList,
		getApplicationTemplates,
		getTemplateContent,
		saveTemplateContent,
	};
}
